import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { lookupInvite, acceptInvite } from "@/lib/settings.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/accept-invite/$token")({
  component: AcceptInvitePage,
});

function AcceptInvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const fetchInvite = useServerFn(lookupInvite);
  const accept = useServerFn(acceptInvite);

  const { data, isLoading } = useQuery({
    queryKey: ["invite", token],
    queryFn: () => fetchInvite({ data: { token } }),
  });

  const [session, setSession] = useState<any>(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, []);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await accept({ data: { token } });
      toast.success("Welcome to the team!");
      navigate({ to: "/command-center" });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed to accept invite");
    } finally {
      setAccepting(false);
    }
  };

  if (isLoading) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  const invite = data?.invite;
  if (!invite) {
    return <Centered title="Invite not found" body="This link is invalid or has been deleted." />;
  }
  if (invite.revoked_at) return <Centered title="Invite revoked" body="The account owner revoked this invite." />;
  if (invite.accepted_at) return <Centered title="Already accepted" body="This invite has already been used." />;
  if (new Date(invite.expires_at) < new Date()) {
    return <Centered title="Invite expired" body="Ask the account owner to send a fresh invite." />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8">
        <h1 className="text-xl font-semibold">Join {invite.account_name}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          You've been invited as <span className="font-medium text-foreground">{invite.role}</span> for{" "}
          <span className="font-medium text-foreground">{invite.email}</span>.
        </p>

        {!session ? (
          <div className="mt-6 space-y-3">
            <p className="text-sm text-muted-foreground">Sign in or create your account to accept.</p>
            <Button className="w-full" onClick={() => navigate({ to: "/login", search: { redirect: `/accept-invite/${token}` } as any })}>
              Sign in
            </Button>
            <Button variant="outline" className="w-full" onClick={() => navigate({ to: "/signup", search: { email: invite.email, redirect: `/accept-invite/${token}` } as any })}>
              Create account
            </Button>
          </div>
        ) : (
          <div className="mt-6">
            <Button className="w-full" onClick={handleAccept} disabled={accepting}>
              {accepting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Accept invitation
            </Button>
            <p className="mt-2 text-xs text-muted-foreground">
              Signed in as {session.user?.email}. Must match the invited email.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Centered({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 text-center">
        <h1 className="text-xl font-semibold">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}
